import { useEffect, useRef, useState } from 'react';
import { createSubscribeHandoffUrl } from '../api/subscribeHandoff';

export type ProFeature = 'create-family' | 'invite' | 'recovery';

interface ProModalProps {
  feature: ProFeature;
  onClose: () => void;
}

const COPY: Record<ProFeature, { title: string; body: string }> = {
  'create-family': {
    title: 'Subscribe to create families',
    body: 'A subscription lets you create a family safe, share logins, and add up to 4 more members. Trial available.',
  },
  invite: {
    title: 'Subscribe to invite members',
    body: 'Inviting members to your family safe requires a subscription. Trial available.',
  },
  recovery: {
    title: 'Subscribe to use recovery',
    body: 'Recovery contacts let you regain access to your safe if you forget your password. A subscription is required.',
  },
};

export function ProModal({ feature, onClose }: ProModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const { title, body } = COPY[feature];
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  // Mint a handoff token so the subscribe app can bootstrap a session —
  // without it the user lands on the subscribe app's "unauthorized" page.
  const handleSubscribe = async () => {
    setError(null);
    setLoading(true);
    try {
      const url = await createSubscribeHandoffUrl();
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="pro-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div ref={dialogRef} tabIndex={-1} className="bg-white rounded-hbs-md shadow-xl max-w-md w-full mx-4 p-6">
        <h2 id="pro-modal-title" className="text-lg font-semibold text-hbs-text mb-2">{title}</h2>
        <p className="text-sm text-hbs-text-mute mb-2">{body}</p>
        {error && <p className="text-xs text-red-600 mb-4">{error}</p>}
        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            id="pro-modal-cancel-button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-hbs-text rounded-hbs-md hover:bg-hbs-surface-alt"
          >
            Cancel
          </button>
          <button
            type="button"
            id="pro-modal-subscribe-button"
            onClick={handleSubscribe}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium bg-hbs-primary text-white rounded-hbs-md hover:bg-hbs-primary-hover disabled:opacity-60"
          >
            {loading ? 'Opening…' : 'Subscribe'}
          </button>
        </div>
      </div>
    </div>
  );
}
